import React, { useState, useEffect, useRef } from "react";
import {
  getStoredConsent,
  saveConsent,
  acceptAll,
  declineAll,
  COOKIE_CATEGORIES,
} from "../../Utils/cookieManager";

export default function CookiePreferencesModal({ isOpen, onClose, onSaved }) {
  const [preferences, setPreferences] = useState({
    [COOKIE_CATEGORIES.ESSENTIAL]: true,
    [COOKIE_CATEGORIES.ANALYTICS]: false,
    [COOKIE_CATEGORIES.FUNCTIONAL]: false,
  });

  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  // Sync current stored consent when modal opens
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement;
      const stored = getStoredConsent();
      if (stored?.categories) {
        setPreferences({
          [COOKIE_CATEGORIES.ESSENTIAL]: true,
          [COOKIE_CATEGORIES.ANALYTICS]: Boolean(stored.categories.analytics),
          [COOKIE_CATEGORIES.FUNCTIONAL]: Boolean(stored.categories.functional),
        });
      } else {
        setPreferences({
          [COOKIE_CATEGORIES.ESSENTIAL]: true,
          [COOKIE_CATEGORIES.ANALYTICS]: false,
          [COOKIE_CATEGORIES.FUNCTIONAL]: false,
        });
      }

      // Auto-focus first focusable element inside modal
      setTimeout(() => {
        const focusable = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable && focusable.length > 0) {
          focusable[0].focus();
        }
      }, 50);
    } else {
      // Restore focus on close
      if (previouslyFocusedElementRef.current && typeof previouslyFocusedElementRef.current.focus === "function") {
        previouslyFocusedElementRef.current.focus();
      }
    }
  }, [isOpen]);

  // Accessibility: Handle Escape key & Focus Trap
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === "Tab") {
        const focusables = dialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables || focusables.length === 0) return;

        const firstElement = focusables[0];
        const lastElement = focusables[focusables.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleToggle = (category) => {
    if (category === COOKIE_CATEGORIES.ESSENTIAL) return; // Locked
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSaveCustom = () => {
    saveConsent(preferences, "custom");
    if (onSaved) onSaved(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    if (onSaved) onSaved({ essential: true, analytics: true, functional: true });
    onClose();
  };

  const handleDeclineAll = () => {
    declineAll();
    if (onSaved) onSaved({ essential: true, analytics: false, functional: false });
    onClose();
  };

  return (
    <div
      className="cookie-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className="cookie-modal-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-preferences-title"
        aria-describedby="cookie-preferences-desc"
      >
        {/* Header */}
        <div className="cookie-modal-header">
          <div>
            <h3 id="cookie-preferences-title" className="cookie-modal-title">
              Manage Cookie Preferences
            </h3>
            <p id="cookie-preferences-desc" className="cookie-modal-subtitle">
              Choose which cookies you want to allow. Essential cookies are necessary for core functionality and cannot be disabled.
            </p>
          </div>
          <button
            type="button"
            className="cookie-modal-close-btn"
            onClick={onClose}
            aria-label="Close cookie preferences modal"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="cookie-modal-body">
          {/* Category 1: Essential */}
          <div className="cookie-category-card">
            <div className="cookie-category-header">
              <div className="cookie-category-info">
                <h4 className="cookie-category-title">Essential Cookies</h4>
                <span className="cookie-badge-locked">Always Active</span>
              </div>
              <label className="cookie-switch" aria-label="Essential Cookies (Always Enabled)">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  readOnly
                  aria-checked="true"
                  aria-disabled="true"
                />
                <span className="cookie-slider" />
              </label>
            </div>
            <p className="cookie-category-desc">
              These cookies are necessary for the website and services to function properly and securely.
            </p>
            <ul className="cookie-category-bullets">
              <li>Authentication & user credentials</li>
              <li>Security & fraud prevention</li>
              <li>Session management & form states</li>
            </ul>
          </div>

          {/* Category 2: Analytics */}
          <div className="cookie-category-card">
            <div className="cookie-category-header">
              <div className="cookie-category-info">
                <h4 className="cookie-category-title">Analytics Cookies</h4>
              </div>
              <label className="cookie-switch" aria-label="Toggle Analytics Cookies">
                <input
                  type="checkbox"
                  checked={preferences[COOKIE_CATEGORIES.ANALYTICS]}
                  onChange={() => handleToggle(COOKIE_CATEGORIES.ANALYTICS)}
                  aria-checked={preferences[COOKIE_CATEGORIES.ANALYTICS]}
                />
                <span className="cookie-slider" />
              </label>
            </div>
            <p className="cookie-category-desc">
              Help us understand how visitors interact with our website to measure performance and improve features.
            </p>
            <ul className="cookie-category-bullets">
              <li>Website usage metrics & page view statistics</li>
              <li>Performance tracking & load time diagnostics</li>
              <li>Error detection to improve stability</li>
            </ul>
          </div>

          {/* Category 3: Functional */}
          <div className="cookie-category-card">
            <div className="cookie-category-header">
              <div className="cookie-category-info">
                <h4 className="cookie-category-title">Functional Cookies</h4>
              </div>
              <label className="cookie-switch" aria-label="Toggle Functional Cookies">
                <input
                  type="checkbox"
                  checked={preferences[COOKIE_CATEGORIES.FUNCTIONAL]}
                  onChange={() => handleToggle(COOKIE_CATEGORIES.FUNCTIONAL)}
                  aria-checked={preferences[COOKIE_CATEGORIES.FUNCTIONAL]}
                />
                <span className="cookie-slider" />
              </label>
            </div>
            <p className="cookie-category-desc">
              Enable enhanced personalization, user preferences, and customized application experiences.
            </p>
            <ul className="cookie-category-bullets">
              <li>Theme preferences & visual display settings</li>
              <li>Language preference preservation</li>
              <li>User experience improvements & layout persistence</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="cookie-modal-footer">
          <button
            type="button"
            className="cookie-btn cookie-btn-secondary"
            onClick={handleDeclineAll}
          >
            Decline Optional
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn-primary"
            onClick={handleAcceptAll}
          >
            Accept All
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn-primary-filled"
            onClick={handleSaveCustom}
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
